import { useState } from "react";
import { Bookmark, ChevronRight, CornerUpRight, EllipsisVertical, Forward, Headphones, Mail, MessageSquareText, Pin, SmilePlus, UserPlus, Info } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar } from "./Avatar";
import { EmojiPicker } from "./EmojiPicker";
import { Composer } from "./Composer";
import { Attachments } from "./Attachments";
import { Mrkdwn } from "@/lib/mrkdwn";
import { useChat } from "@/lib/chat";
import { copyText, emojiChar, fmtFull, fmtRelative, fmtTime, pluralize, QUICK_REACTIONS } from "@/lib/format";
import { errMsg } from "@/lib/api";
import { isTouch } from "@/lib/device";
import { analytics } from "@/lib/analytics";

const Reactions = ({ m, meId, onToggle, canReact = true }) => {
  if (!m.reactions?.length) return null;
  return (
    <div className="tc-reactions" data-testid="reactions-row">
      {m.reactions.map((r) => (
        <button key={r.name} className={`tc-reaction${r.users.includes(meId) ? " is-mine" : ""}`} onClick={() => (canReact ? onToggle(r.name) : toast.error("You don't have permission to add reactions. Ask a workspace admin."))} title={`:${r.name}:`} data-testid={`reaction-${r.name}`} aria-disabled={!canReact}>
          <span className="tc-reaction__emoji">{emojiChar(r.name) || `:${r.name}:`}</span>
          <span>{r.users.length}</span>
        </button>
      ))}
      {canReact && <EmojiPicker onPick={(name) => onToggle(name)} align="start">
        <button className="tc-reaction is-add" title="Add reaction" data-testid="add-reaction-inline"><SmilePlus /></button>
      </EmojiPicker>}
    </div>
  );
};

const SystemLine = ({ m, ctx }) => {
  const Icon = m.subtype === "huddle" ? Headphones : m.text.includes("joined") || m.text.includes("added") ? UserPlus : m.text.includes("pinned") ? Pin : Info;
  return (
    <div className="tc-msg__system" data-testid="system-message">
      <Icon />
      <span><Mrkdwn text={m.text} ctx={ctx} /></span>
      <span className="tc-msg__time">{fmtTime(m.created_at)}</span>
    </div>
  );
};

export const MessageItem = ({ m, grouped, ctx, inThread, hideThreadIndicator, compact }) => {
  const { meId, toggleReaction, togglePin, toggleSave, deleteMessage, editMessage, openThread, openProfile, setModal, me, can } = useChat();
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const mine = m.user_id === meId;
  const highlight = !mine && !m.subtype && (m.mentions?.includes(meId) || m.mention_channel || m.mention_here);

  const onToggle = (name) => toggleReaction(m.id, name).catch((e) => toast.error(errMsg(e)));
  const onDelete = async () => {
    if (!window.confirm("Delete this message? This can't be undone.")) return;
    try { await deleteMessage(m.id); toast.success("Message deleted"); } catch (e) { toast.error(errMsg(e)); }
  };
  const copyLink = () => { copyText(`${window.location.origin}/client/${m.conversation_id}?m=${m.id}`); toast.success("Link copied"); };

  if (m.subtype === "system" || m.subtype === "huddle") {
    return <div className="tc-msg is-grouped" data-testid={`message-${m.id}`}><div className="tc-msg__row"><div className="tc-msg__gutter" /><div className="tc-msg__body"><SystemLine m={m} ctx={ctx} /></div></div></div>;
  }

  const email = m.subtype === "email" ? m.meta?.email : null;
  const fwd = m.subtype === "forward" ? m.meta?.forwarded : null;
  const displayName = m.bot_name || m.user?.display_name || "Unknown";

  return (
    <div className={`tc-msg${grouped ? " is-grouped" : ""}${highlight ? " is-highlight" : ""}${editing ? " is-editing" : ""}${menuOpen ? " is-menu-open" : ""}${m.pending ? " is-pending" : ""}`} data-pending={m.pending ? "true" : undefined} data-testid={`message-${m.id}`} id={`msg-${m.id}`}
      onClick={(e) => { if (!isTouch() || e.target.closest("a,button,input,textarea,[role=menuitem],.tc-msg__actions")) return; setMenuOpen((o) => !o); }}>
      {m.pinned && !grouped && !compact && <div className="tc-msg__pinned" data-testid="pinned-label"><Pin /> Pinned by {m.pinned_by === meId ? "you" : ctx.usersById?.[m.pinned_by]?.display_name || "a member"}</div>}
      <div className="tc-msg__row">
      <div className="tc-msg__gutter">
        {grouped ? <span className="tc-msg__hovertime">{fmtTime(m.created_at).replace(/ (AM|PM)/, "")}</span> : <Avatar user={m.user} size={36} onClick={() => m.user && openProfile(m.user.id)} data-testid="message-avatar" />}
      </div>
      <div className="tc-msg__body">
        {!grouped && (
          <div className="tc-msg__head">
            <button className="tc-msg__author" onClick={() => m.user && openProfile(m.user.id)} data-testid="message-author">{displayName}</button>
            {(m.user?.is_bot || m.bot_name) && <span className="tc-msg__app">App</span>}
            <button className="tc-msg__time" title={fmtFull(m.created_at)} onClick={copyLink} data-testid="message-time">{fmtTime(m.created_at)}</button>
            {m.pending && <span className="tc-msg__pending" data-testid="message-pending">Sending when back online…</span>}
          </div>
        )}
        {editing ? (
          <div className="tc-inline-edit">
            <Composer conversationId={m.conversation_id} parentId={m.parent_id} initialText={m.text} editing compact autoFocus onCancel={() => setEditing(false)} onSend={async (t) => { await editMessage(m.id, t); setEditing(false); }} />
          </div>
        ) : (
          <>
            {email ? (
              <div className="tc-email-card" data-testid="email-message-card">
                <div className="tc-email-card__head"><Mail /><div><div className="tc-email-card__subject">{email.subject}</div><div className="tc-email-card__meta">From {email.from_name && email.from_name !== email.from ? `${email.from_name} <${email.from}>` : email.from} · to {email.to}{email.in_thread ? " · reply" : ""}</div></div></div>
                {m.text && <div className="tc-email-card__body">{m.text}</div>}
                {m.files?.length > 0 && <div className="tc-email-card__files"><Attachments files={m.files} compact={compact} /></div>}
              </div>
            ) : (
              <>
                {m.text && <span style={{ display: "inline" }}><Mrkdwn text={m.text} ctx={ctx} messageId={m.id} />{m.edited_at && <span className="tc-msg__edited">(edited)</span>}</span>}
                {!m.text && m.edited_at && <span className="tc-msg__edited">(edited)</span>}
                <Attachments files={m.files} compact={compact} />
                {fwd && (
                  <div className="tc-forward" data-testid="forwarded-message">
                    <div className="tc-msg__head"><Avatar user={fwd.author} size={20} radius={4} /><span className="tc-msg__author" style={{ fontSize: 14 }}>{fwd.author?.display_name}</span><span className="tc-msg__time">{fmtTime(fwd.created_at)}</span></div>
                    <Mrkdwn text={fwd.text} ctx={ctx} />
                    <Attachments files={fwd.files} compact />
                    <div className="tc-forward__meta">Forwarded from {fwd.from?.type === "channel" ? `#${fwd.from.name}` : fwd.from?.display_name}</div>
                  </div>
                )}
              </>
            )}
            <Reactions m={m} meId={meId} onToggle={onToggle} canReact={can("reactions.add")} />
            {!inThread && !hideThreadIndicator && m.reply_count > 0 && (
              <button className="tc-thread-ind" onClick={() => openThread(m.id, m.conversation_id)} data-testid="thread-indicator">
                <span className="tc-thread-ind__avatars">{(m.reply_users_info || []).slice(0, 3).map((u) => <Avatar key={u.id} user={u} size={24} radius={5} />)}</span>
                <span className="tc-thread-ind__count">{pluralize(m.reply_count, "reply").replace("replys", "replies")}</span>
                <span className="tc-thread-ind__last">Last reply {fmtRelative(m.last_reply_at)}</span>
                <span className="tc-thread-ind__view">View thread <ChevronRight size={14} /></span>
              </button>
            )}
          </>
        )}
      </div>
      </div>
      {!editing && (
        <div className="tc-msg__actions" data-testid="message-actions">
          {can("reactions.add") && QUICK_REACTIONS.map((r) => <button key={r} className="tc-msg__action" title={`:${r}:`} onClick={() => onToggle(r)} data-testid={`quick-reaction-${r}`}>{emojiChar(r)}</button>)}
          {can("reactions.add") && <EmojiPicker onPick={(name) => onToggle(name)}>
            <button className="tc-msg__action" title="Find another reaction" data-testid="action-add-reaction"><SmilePlus /></button>
          </EmojiPicker>}
          {!inThread && <button className="tc-msg__action" title="Reply in thread" onClick={() => openThread(m.id, m.conversation_id)} data-testid="action-reply-thread"><MessageSquareText /></button>}
          <button className="tc-msg__action" title="Forward message" onClick={() => setModal({ type: "forward", message: m })} data-testid="action-forward"><Forward /></button>
          <button className={`tc-msg__action${m.is_saved ? " is-on" : ""}`} title={m.is_saved ? "Remove from Later" : "Save for later"} onClick={() => toggleSave(m).catch((e) => toast.error(errMsg(e)))} data-testid="action-save"><Bookmark fill={m.is_saved ? "currentColor" : "none"} /></button>
          <DropdownMenu onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild><button className="tc-msg__action" title="More actions" data-testid="action-more"><EllipsisVertical /></button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="tc-menu">
              {!inThread && <DropdownMenuItem className="tc-menu__item" onClick={() => openThread(m.id, m.conversation_id)}>Reply in thread</DropdownMenuItem>}
              <DropdownMenuItem className="tc-menu__item" onClick={() => setModal({ type: "forward", message: m })}>Forward message…</DropdownMenuItem>
              <DropdownMenuSeparator className="tc-menu__sep" />
              <DropdownMenuItem className="tc-menu__item" onClick={copyLink}>Copy link</DropdownMenuItem>
              <DropdownMenuItem className="tc-menu__item" onClick={() => { copyText(m.text); analytics.track("message_text_copied", { target_id: m.id, conversation_id: m.conversation_id }); toast.success("Text copied"); }}>Copy text</DropdownMenuItem>
              <DropdownMenuSeparator className="tc-menu__sep" />
              {!m.parent_id && <DropdownMenuItem className="tc-menu__item" onClick={() => togglePin(m.id).catch((e) => toast.error(errMsg(e)))} data-testid="action-pin">{m.pinned ? "Unpin from channel" : "Pin to channel"}</DropdownMenuItem>}
              <DropdownMenuItem className="tc-menu__item" onClick={() => toggleSave(m)}>{m.is_saved ? "Remove from Later" : "Save for later"}</DropdownMenuItem>
              {((mine && (can("messages.edit_own") || can("messages.delete_own"))) || (!mine && can("messages.delete_any"))) && (
                <>
                  <DropdownMenuSeparator className="tc-menu__sep" />
                  {mine && !email && can("messages.edit_own") && <DropdownMenuItem className="tc-menu__item" onClick={() => setEditing(true)} data-testid="action-edit">Edit message</DropdownMenuItem>}
                  {(mine ? can("messages.delete_own") : can("messages.delete_any")) && <DropdownMenuItem className="tc-menu__item is-danger" onClick={onDelete} data-testid="action-delete">Delete message…</DropdownMenuItem>}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      {false && <CornerUpRight />}
    </div>
  );
};
